import { clamp01, lerp } from './math.js';
import type { BallPoint } from '../player/model.js';

export interface LiveFrameWindow {
  fromIndex: number;
  toIndex: number;
  amount: number;
}

export function interpolateLivePlayers(
  startPlayers: any[] = [],
  endPlayers: any[] = [],
  amount: number,
): any[] {
  const blend = clamp01(amount);
  if (blend <= 0 || !endPlayers.length) {
    return startPlayers.map((player) => ({ ...player }));
  }
  const endByRole = new Map(endPlayers.map((player) => [player.role, player]));
  return startPlayers.map((player) => {
    const end = endByRole.get(player.role);
    if (!end) return { ...player };
    return {
      ...player,
      x: lerp(Number(player.x) || 0, Number(end.x) || 0, blend),
      y: lerp(Number(player.y) || 0, Number(end.y) || 0, blend),
      arcadeVerticalLift: lerp(
        Number(player.arcadeVerticalLift) || 0,
        Number(end.arcadeVerticalLift) || 0,
        blend,
      ),
      arcadeVisualVx: lerp(
        Number(player.arcadeVisualVx) || 0,
        Number(end.arcadeVisualVx) || 0,
        blend,
      ),
      arcadeVisualVy: lerp(
        Number(player.arcadeVisualVy) || 0,
        Number(end.arcadeVisualVy) || 0,
        blend,
      ),
    };
  });
}

export function liveFrameWindow(progress: number, frameCount: number): LiveFrameWindow {
  if (frameCount <= 1) {
    return { fromIndex: 0, toIndex: 0, amount: 0 };
  }

  const scaled = clamp01(progress) * (frameCount - 1);
  const fromIndex = Math.floor(scaled);
  const toIndex = Math.min(frameCount - 1, fromIndex + 1);

  return {
    fromIndex,
    toIndex,
    amount: scaled - fromIndex,
  };
}

export function interpolateBallPoint(start: BallPoint, end: BallPoint, amount: number): BallPoint {
  const blend = clamp01(amount);
  const startHeight = Number(start.arcadeHeight);
  const endHeight = Number(end.arcadeHeight);
  const startGroundY = Number(start.arcadeGroundY);
  const endGroundY = Number(end.arcadeGroundY);

  return {
    ballX: lerp(start.ballX, end.ballX, blend),
    ballY: lerp(start.ballY, end.ballY, blend),
    ...(
      Number.isFinite(startHeight) && Number.isFinite(endHeight)
        ? { arcadeHeight: lerp(startHeight, endHeight, blend) }
        : {}
    ),
    ...(
      Number.isFinite(startGroundY) && Number.isFinite(endGroundY)
        ? { arcadeGroundY: lerp(startGroundY, endGroundY, blend) }
        : {}
    ),
  };
}
